import { View, Text, styled, Image } from 'tamagui'
import { FileText, Play, Download } from '@tamagui/lucide-icons'
import { useAppStore } from '@/store'
import type { Attachment } from '@/types'

const AttachmentContainer = styled(View, {
  marginTop: 4,
  gap: 4,
})

const ImageWrapper = styled(View, {
  borderRadius: '$borderRadiusMd',
  overflow: 'hidden',
  maxWidth: 400,
  maxHeight: 300,
  cursor: 'pointer',
  position: 'relative',
})

const AttachmentImage = styled(Image, {
  borderRadius: '$borderRadiusMd',
  maxWidth: 400,
  maxHeight: 300,
})

const GifBadge = styled(View, {
  position: 'absolute',
  top: 8,
  left: 8,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 6,
  paddingVertical: 2,
})

const GifBadgeText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '700',
  color: 'white',
})

const VideoWrapper = styled(View, {
  borderRadius: '$borderRadiusMd',
  overflow: 'hidden',
  maxWidth: 400,
  maxHeight: 300,
  cursor: 'pointer',
  position: 'relative',
  backgroundColor: '$sidebar',
  alignItems: 'center',
  justifyContent: 'center',
})

const PlayOverlay = styled(View, {
  position: 'absolute',
  width: 48,
  height: 48,
  borderRadius: 24,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  alignItems: 'center',
  justifyContent: 'center',
})

const FileCard = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  paddingHorizontal: 12,
  paddingVertical: 10,
  maxWidth: 400,
})

const FileInfo = styled(View, {
  flex: 1,
  minWidth: 0,
})

const FileName = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  fontWeight: '500',
  color: '$accent',
  cursor: 'pointer',
  hoverStyle: {
    textDecorationLine: 'underline',
  },
  // @ts-expect-error -- web text-overflow
  style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
})

const FileSize = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 11,
  color: '$color8',
})

const DownloadButton = styled(View, {
  width: 32,
  height: 32,
  borderRadius: '$borderRadiusSm',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
})

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface MessageAttachmentProps {
  attachments: Attachment[]
}

export function MessageAttachment({ attachments }: MessageAttachmentProps) {
  const { setImagePreviewUrl } = useAppStore()

  return (
    <AttachmentContainer>
      {attachments.map((attachment) => {
        switch (attachment.type) {
          case 'image':
            return (
              <ImageWrapper
                key={attachment.id}
                onPress={() => setImagePreviewUrl(attachment.url)}
              >
                <AttachmentImage
                  source={{ uri: attachment.url, width: Math.min(attachment.width || 400, 400), height: Math.min(attachment.height || 300, 300) }}
                  resizeMode="cover"
                />
              </ImageWrapper>
            )

          case 'gif':
            return (
              <ImageWrapper
                key={attachment.id}
                onPress={() => setImagePreviewUrl(attachment.url)}
              >
                <AttachmentImage
                  source={{ uri: attachment.url, width: Math.min(attachment.width || 400, 400), height: Math.min(attachment.height || 300, 300) }}
                  resizeMode="cover"
                />
                <GifBadge>
                  <GifBadgeText>GIF</GifBadgeText>
                </GifBadge>
              </ImageWrapper>
            )

          case 'video':
            return (
              <VideoWrapper key={attachment.id}>
                {attachment.thumbnailUrl ? (
                  <AttachmentImage
                    source={{ uri: attachment.thumbnailUrl, width: Math.min(attachment.width || 400, 400), height: Math.min(attachment.height || 300, 300) }}
                    resizeMode="cover"
                  />
                ) : (
                  <View width={400} height={225} backgroundColor="$sidebar" />
                )}
                <PlayOverlay>
                  <Play size={24} color="white" fill="white" />
                </PlayOverlay>
              </VideoWrapper>
            )

          case 'file':
            return (
              <FileCard key={attachment.id}>
                <FileText size={24} color="$accent" />
                <FileInfo>
                  <FileName onPress={() => window.open(attachment.url, '_blank')}>
                    {attachment.filename}
                  </FileName>
                  <FileSize>{formatFileSize(attachment.size)}</FileSize>
                </FileInfo>
                <DownloadButton onPress={() => window.open(attachment.url, '_blank')}>
                  <Download size={16} color="$sidebarMuted" />
                </DownloadButton>
              </FileCard>
            )

          default:
            return null
        }
      })}
    </AttachmentContainer>
  )
}
